import { useCallback, useEffect, useRef } from 'react';

import { AllCardsRows } from '@/features/cards/components/AllCards/AllCardsRows';
import { AllCardsHeaders } from '@/features/cards/components/AllCards/AllCardsHeaders';
import { isCardSortKeyAvailable, ISearchCard } from '@/features/cards/store/models/card.model';
import { ALL_CARDS_PER_PAGE } from '@/features/cards/constants';
import { parseAllCardFiltersValue, useAllCardsFilters } from '@/features/cards/hooks/AllCards/useAllCardsFilters';
import { searchAgentCards } from '@/features/cards/store/actions/agent-card.actions';
import { IAllCardsFiltersFormFields, TAllCardsFiltersFields } from '@/features/cards/store/types/agent-card.types';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { parseAllParameters, TUseTableDataReturn } from '@/ui-kit/table-filters/filters.type';
import { allcardsHeadersList, TAllCardsRowType } from '@/features/cards/store/models/allcards.model';

export function useAllCardsTableData(
  defaultFilters: Partial<IAllCardsFiltersFormFields> = {},
): TUseTableDataReturn<TAllCardsRowType, TAllCardsFiltersFields> {
  const dispatch = useAppDispatch();

  const hasData = useAppSelector((state) => state.agentCard.paginateCards.elements).length > 0;
  const paymentCards = useAppSelector((state) => state.agentCard.paginateCards.elements);
  const pagination = useAppSelector((state) => state.agentCard.paginateCards.pagination);
  const isLoading = useAppSelector((state) => state.agentCard.isLoading);
  const error = useAppSelector((state) => state.agentCard.error);

  const queryParameters = useRef({
    pageNumber: undefined,
    filters: parseAllParameters<IAllCardsFiltersFormFields, ISearchCard>(defaultFilters, parseAllCardFiltersValue),
  });

  const filtersDeps = JSON.stringify(defaultFilters);

  useEffect(() => {
    const filters = JSON.parse(filtersDeps);
    queryParameters.current = {
      pageNumber: 1,
      filters: parseAllParameters<IAllCardsFiltersFormFields, ISearchCard>(filters, parseAllCardFiltersValue),
    };
    dispatch(searchAgentCards({ pageNumber: queryParameters.current.pageNumber, ...queryParameters.current.filters }));
  }, [dispatch, filtersDeps]);

  const onPageChange = useCallback(
    (pageNumber: number) => {
      queryParameters.current = {
        ...queryParameters.current,
        pageNumber,
      };
      const { filters, ...parameters } = queryParameters.current;
      dispatch(searchAgentCards({ ...parameters, ...filters }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (filtersParameters: ISearchCard) => {
      queryParameters.current = {
        ...queryParameters.current,
        filters: filtersParameters,
        pageNumber: 1,
      };
      const { filters, ...parameters } = queryParameters.current;
      dispatch(searchAgentCards({ ...parameters, ...filters }));
    },
    [dispatch],
  );

  const onSort = useCallback(
    (field: keyof TAllCardsRowType, isSortedAtoZ: boolean) => {
      if (isCardSortKeyAvailable(field)) {
        dispatch(
          searchAgentCards({
            sortBy: field,
            sortOrder: isSortedAtoZ ? 'DESC' : 'ASC',
          }),
        );
      }
    },
    [dispatch],
  );

  const onPerPage = undefined;
  const perPage = ALL_CARDS_PER_PAGE;

  const headers = AllCardsHeaders({ headersList: allcardsHeadersList });
  const rows = AllCardsRows({ paymentCards, isLoading });

  const { status, type, search, createdDate, clearFilters, hasFilters } = useAllCardsFilters(
    handleFilterChange,
    defaultFilters,
  );

  return {
    data: paymentCards,
    pagination,
    isLoading,
    hasData,
    error,
    headers,
    rows,
    onPageChange,
    clearFilters,
    onSort,
    onPerPage,
    perPage,
    fields: {
      status,
      type,
      createdDate,
      search,
    },
    hasFilters,
  };
}
